/*
ゆめさくコードの帯に並ぶ項目の表。**PC の帯とスマホの引き出しは、この表だけを読む。**

前は PC とスマホで別々に書いていて、PC に AIスキルアップ、スマホに AI・IT用語辞典と
ご意見ボックスが出ていた (2026-10-20 の棚卸し)。項目を足すときは、ここに足す。
項目を足すときは、行き先の頁に中身があるかを先に見る
*/

import type { NavIconName } from "../../base/HandIcon/NavIcon";

export interface HeaderNavItem {
  id: string;
  to: string;
  label: string;
  /** 帯の項目だけで出す短い表記 (「CS」)。無ければ label を出す。読み上げの名前 (aria-label) は label のまま */
  barLabel?: string;
  /** 字の前に付ける線の印。/women の帯 (shegate-web の GlobalHeader) と同じ NavIcon の絵。引き出しの組の中では付けない */
  icon?: NavIconName;
  /** 引き出しで 1 行を 1 つで使う (2 列に並べると字が収まらない項目) */
  wide?: boolean;
  /**
   * 行き先が別オリジンの絶対 URL。react-router の Link に絶対 URL をそのまま渡すと
   * パス解決が壊れ、SPA の中の存在しない経路へ飛ぼうとする。true のときは PageHeader 側で
   * 素の a タグとして出す (タブは開いたまま、同じ窓)
   */
  external?: boolean;
  /**
   * 帯でこの項目自体が窓 (ドロップダウン) の入口になる (コース一覧、転職)。
   * resolveActiveNav の住所あわせの対象から外す。入口の to は中の子の 1 つと同じ住所に
   * なりがちで、外さないと「どちらが長く一致するか」の判定があいまいになる。
   * 今いる場所の印は、子の項目の活性を PageHeader 側で入口にも足して付ける
   */
  isMenuTrigger?: boolean;
  /** 窓と引き出しで名前の下に添える、何の頁かの短い 1 行。窓の幅に収まる長さにする (折り返さない) */
  description?: string;
  /** 窓の入口 (isMenuTrigger) だけが持つ、窓の中身 */
  menu?: HeaderNavMenu;
  /**
   * この項目のほかに、帯でこの項目を光らせる項目の id。窓の中身が静的な一覧でない「コース」の窓の中の行
   * (コンピュータサイエンス) を見ているときに、帯の「コース」を光らせるため
   */
  activeFor?: readonly string[];
}

export interface HeaderNavMenu {
  /** 窓の一覧の読み上げの名前 */
  label: string;
  items: HeaderNavItem[];
  /** 窓の下に置く「すべて見る」の行。絵を付けない */
  more?: HeaderNavItem;
  /** 入口を今いる場所として光らせる、中の項目の id */
  activeIds: ReadonlySet<string>;
}

/**
 * 窓と引き出しの項目に添える絵。行き先の頁の帯や札と同じ絵を、apps/client が id ごとに渡す
 * (絵の住所は生成物の名前に中身のハッシュが入るので、shared-ui には持たない)。
 * 絵は描かない。今ある画像を object-fit: cover で 2 対 1 の枠に切り抜くだけ
 */
export interface HeaderNavArt {
  src: string;
  /** 暗い版の絵があるときだけ。帯が暗い版のときに差し替える */
  darkSrc?: string;
  /** 切り抜く位置 (object-position)。横長の帯の絵は、人物や主役の物がある所を指す */
  position?: string;
}

export interface HeaderNavGroup {
  id: string;
  /** 見出し。空なら見出しを出さない */
  label: string;
  items: HeaderNavItem[];
}

export interface HeaderNav {
  /** PC の帯 */
  bar: HeaderNavItem[];
  /** スマホの引き出し。組ごとに見出しを付ける */
  drawer: HeaderNavGroup[];
}

/*
コーディング面接問題の頁。別の担当が作っている (2026-10-24 時点で main に無い)。
頁が main に入るまで、この入口を本番に出さない (404 の入口になる)
*/
export const CODING_INTERVIEW_HREF = "/interview-questions/coding";

/*
セキュリティを学べる場所の入口。2026-10-24 の代表の決めで、既存のコースとは別の独自のサイトにした。
同じ日に security.yumesaku.tech から yumesaku.tech/security へ移した (代表「yumesaku.tech/security がいいな」)。
住所は同じオリジンでも、/security の下は別の Worker (yumesaku-security) が出しているので、
react-router の Link で SPA の中を動かすと頁が無い。external を true にして素の a で開く
*/
export const SECURITY_TRACK_HREF = "/security/";

/*
IT資格取得の入口 (2026-10-25 の代表の指示で帯と引き出しとフッターに置く。狙いは検索から来る人)。試験の頁は /certification/<slug>。
試験の並びと名前の正本はここ (フッターの「IT資格取得」の列が読む。apps/client の lib/certification.ts は事実と中身を持つ)。
並びは Google 広告の月の検索数 (日本、日本語、2026-10-25) の多い順。数は試験名の頭の語の数
(AWS は 2 つとも 8,100 で、入口の CLF を先に置く。応用情報と LinuC も 74,000 で並ぶ)
2026-10-29 に AWS の AI の 2 試験 (AIF と AIP) を足した。数は「aws ai practitioner」2,400 と「aws aip」480 (2026-10-29)。
並びの決まり (headerNav.test.ts が見る)
  group を付けた試験は塊にして続けて並べる。塊はその中でいちばん大きい数の位置に置き、塊の中も数の多い順
  group の無い試験は 1 本で 1 つの塊。group は一覧で並べて見せたい主催にだけ付ける (今は AWS だけ。IPA の試験は付けず数の順のまま)
*/
export const CERTIFICATION_HREF = "/certification";

export interface CertificationExamLink {
  slug: string;
  /** フッターと札の題に出す名前 */
  label: string;
  /** 頭の語の月の検索数 (並べる順の根拠。画面には出さない) */
  monthlySearches: number;
  /** フッターの狭い列で折ってよい所で区切った名前 (区切りの間でだけ折る)。無ければ label を空白で折る */
  breakParts?: readonly string[];
  /** 続けて並べる塊の名前 (同じ主催の試験)。並びの決まりは上のコメント */
  group?: string;
}

export const CERTIFICATION_EXAMS: readonly CertificationExamLink[] = [
  { slug: "it-passport", label: "ITパスポート", monthlySearches: 246_000 },
  { slug: "fe", label: "基本情報技術者試験", monthlySearches: 165_000 },
  { slug: "ap", label: "応用情報技術者試験", monthlySearches: 74_000 },
  { slug: "linuc", label: "LinuC", monthlySearches: 74_000 },
  { slug: "generative-ai-passport", label: "生成AIパスポート", monthlySearches: 74_000 },
  { slug: "g-kentei", label: "G検定", monthlySearches: 60_500 },
  { slug: "sc", label: "情報処理安全確保支援士", monthlySearches: 33_100, breakParts: ["情報処理", "安全確保支援士"] },
  { slug: "sg", label: "情報セキュリティマネジメント試験", monthlySearches: 27_100, breakParts: ["情報セキュリティ", "マネジメント試験"] },
  { slug: "ccna", label: "CCNA", monthlySearches: 22_200 },
  { slug: "toukei-kentei", label: "統計検定", monthlySearches: 18_100 },
  { slug: "aws-clf", label: "AWS クラウドプラクティショナー", monthlySearches: 8_100, breakParts: ["AWS ", "クラウドプラクティショナー"], group: "aws" },
  { slug: "aws-saa", label: "AWS ソリューションアーキテクト アソシエイト", monthlySearches: 8_100, breakParts: ["AWS ソリューション", "アーキテクト アソシエイト"], group: "aws" },
  { slug: "aws-aif", label: "AWS AI プラクティショナー", monthlySearches: 2_400, breakParts: ["AWS AI ", "プラクティショナー"], group: "aws" },
  { slug: "aws-aip", label: "AWS 生成AIデベロッパー プロフェッショナル", monthlySearches: 480, breakParts: ["AWS 生成AI", "デベロッパー プロフェッショナル"], group: "aws" },
  { slug: "lpic", label: "LPIC-1", monthlySearches: 6_600 },
  { slug: "python3", label: "Python 3 エンジニア認定基礎試験", monthlySearches: 4_400, breakParts: ["Python 3 エンジニア", "認定基礎試験"] },
  { slug: "java-silver", label: "Java Silver", monthlySearches: 3_600 },
  // IPA が 2027年度から始める予定の 2試験 (2026-10-29 に足した)。数は 2026-10-29 の Google 広告の月の検索数
  { slug: "data-management", label: "データマネジメント試験", monthlySearches: 1_900, breakParts: ["データ", "マネジメント試験"] },
  { slug: "professional-digital-skills", label: "プロフェッショナルデジタルスキル試験", monthlySearches: 1_900, breakParts: ["プロフェッショナル", "デジタルスキル試験"] },
];

export function certificationExamHref(slug: string): string {
  return `${CERTIFICATION_HREF}/${slug}`;
}

/*
2026-10-24 の代表の決め (案 1)。「header 整理もしたいよね」「技術面接問題 コーディング面接問題 を header に必要」。
- 帯は 5 つ。コース一覧、技術面接問題、コーディング面接問題、AI で作る、料金。語は代表の言葉のまま
- 技術面接問題の行き先は、代表が正と決めた /interview-questions
- 引き出しは 学ぶ、腕試し、調べる、仕事 の組に見出しを付ける。ヘッダーからたどれなかった頁 (Git コマンド辞典、AI で作る、
  練習問題、タイピングなど) をここに集める
- 求人 (/jobs) は 10/20 に帯から外した (学生向けのインターンが多い)。引き出しの「仕事」の組には残す
前の経緯 (10/20)。5 項目と棚 2 枚から 2 項目 (コース一覧、料金) にした。代表の「少ない構成でいい」

2026-10-24 (同日もう1つ)。代表の指示「header にセキュリティ学習を追加」。
- 帯は 6 つに増える。置き場は「コース一覧」の次 (学ぶ物の並び)
- 行き先は SECURITY_TRACK_HREF。印は今ある原料で描いた南京錠 (NavIcon の "security")
- 引き出しは「学ぶ」の組に足す。腕試し・調べる・仕事の組は変えない
- 6 つ目が増えて 1100px の帯では収まらなかったので、PC の帯を出し始める幅を 1280px に広げ、
  項目の左右の余白も詰めた (PageHeader.tsx の DRAWER_CLOSE_QUERY と min-[1280px]: の並びを見る)
- 同じ日に代表の直し「独自サイトにして、既存の security course は関係ない」。公開の作業中は帯と引き出しから外していたが、
  いま yumesaku.tech/security へ向けて元の位置 (コース一覧の次) に戻した。1280px の幅はそのまま
*/
/*
2026-10-24 の代表の指示「header に 転職 を作りたいよね」。3 段階で直した。
1. 最初は引き出しの「仕事」の組だけに転職診断・転職ロードマップの 2 つを足した
2. 同じ日のうちに「PC の帯にも転職を出したい」と直しが入り、転職診断を単独の項目として
   coding の次に足した。7 項目になり、ROW_INNER (1152px 頭打ち) の中で右の釦の組 (検索・
   ログイン・新規登録) が押し出されて壊れた (「ログイン」が縦に1字ずつ、新規登録の釦が2行になる)
3. 同じ日にもう一度「PC の帯に『転職』のドロップダウンを置く」に決まった。単独リンクでなく、
   「コース一覧」の CoursesBarItem と同じ窓の仕組みを使い回したドロップダウンにして、
   転職診断・転職ロードマップ・求人・キャリアの歩き方の 4 つを中に入れる。帯には「転職」の
   2 文字だけが増える。それでも 1280px では右の釦の組がまだ少し押し出されたので、
   PageHeader.tsx 側で帯の項目の gap と padding を詰めて幅を作り、1280/1366/1440/1536px で
   折り返さないことを確かめた (詰めた場所は PageHeader.tsx のその場所のコメントを見る)

仕事の組の 4 項目 (diagnosis/roadmap/jobs/careerPath) は、引き出しの「仕事」の組と
「転職」の窓の中身の両方で同じオブジェクトを共有する。モジュール直下の export にしたのは、
apps/client の窓の中身 (CareerMenuPanel) からも同じ項目を読むため
*/
export const CAREER_DIAGNOSIS: HeaderNavItem = { id: "career-diagnosis", to: "/career-change/diagnosis", label: "転職診断", description: "答えて、いまの段を知る" };
export const CAREER_ROADMAP: HeaderNavItem = { id: "career-roadmap", to: "/career-change", label: "転職ロードマップ", description: "未経験から転職までの 6 つの段" };
export const CAREER_JOBS: HeaderNavItem = { id: "jobs", to: "/jobs", label: "求人", description: "エンジニアのインターンと求人" };
export const CAREER_PATH_GUIDE: HeaderNavItem = { id: "career", to: "/learn/courses/career-path-guide", label: "キャリアの歩き方", description: "キャリアの道すじを学ぶ無料コース" };
/** 「転職」の窓の中身。並びは診断を先頭に (申し込みに近い順) */
export const CAREER_MENU_ITEMS: HeaderNavItem[] = [CAREER_DIAGNOSIS, CAREER_ROADMAP, CAREER_JOBS, CAREER_PATH_GUIDE];
/**
 * 「転職」の帯の入口を光らせる、窓の中の子の id。resolveActiveNav は窓の入口 (isMenuTrigger) を
 * 候補から外して診断/ロードマップのどちらかの id を返すので、PageHeader 側で「入口も一緒に光らせる」判定に使う。
 * 求人・キャリアの歩き方は住所がまったく別 (/jobs, /learn/courses/career-path-guide) で、
 * その頁を見ているときに「転職」まで光らせるのは今いる場所の印として広すぎるので含めない
 */
export const CAREER_MENU_ACTIVE_CHILD_IDS: ReadonlySet<string> = new Set([CAREER_DIAGNOSIS.id, CAREER_ROADMAP.id]);

/*
2026-10-26 の代表の指示「IT資格とセキュリティも hover で何か出てほしい」。セキュリティも IT資格と同じ窓にする。
中の 4 つは本番 yumesaku.tech/security の構成から選んだ、学ぶ順の地図 (入口そのもの)、攻撃の種類の一覧、
事例データベース、ハッキングを合法に学ぶ道筋。窓の下に、入口の「最初のレッスンを読む」(脅威モデリング) への行を置く。
/security の下は別の Worker (yumesaku-security、apps/security-astro) が出す別アプリ。IT資格・面接対策・転職は
同じ SPA (react-router) の中の住所だが、/security は違うので、窓の中の行もすべて external にして素の a で開く
(react-router の Link に絶対でない別アプリの住所を渡すと、SPA の中の存在しない経路へ飛ぼうとして壊れる)。
絵は apps/security-astro/public/fig の本物の画像を、apps/client/public/assets/security-nav へそのままコピーして使う
(描き直さない)。/security 自身の画像を窓の絵として直接 cross-worker で読むと、手元の開発サーバーには
/security 用のプロキシが無く絵が出ないため、他の窓の絵 (career-change-band など) と同じく client 自身の public に置く。
事例 (cases) とハッキングを合法に学ぶ (learn-hacking) は、2 対 1 の窓の絵に合う専用の画像が今の /security に無いので、絵を付けない
*/
const SECURITY_MENU_PICKS: readonly { id: string; to: string; label: string; description: string }[] = [
  { id: "security-learn", to: SECURITY_TRACK_HREF, label: "学ぶ順", description: "5 つのトラックを順に" },
  { id: "security-attacks", to: "/security/attacks/", label: "攻撃の種類", description: "ランサムウェアやフィッシングの流れ" },
  { id: "security-cases", to: "/security/cases/", label: "事例", description: "実際に起きた事故から学ぶ" },
  { id: "security-learn-hacking", to: "/security/learn-hacking/", label: "ハッキングを合法に学ぶ", description: "許された場で腕を試す道筋" },
];
export const SECURITY_MENU_ITEMS: HeaderNavItem[] = SECURITY_MENU_PICKS.map((p) => ({ ...p, external: true }));
/** 窓の下に置く、入口が勧める最初のレッスン (脅威モデリング) への行 */
export const SECURITY_LESSON_HREF = "/security/lessons/threat-modeling/";
export const SECURITY_MENU_MORE: HeaderNavItem = { id: "security-first-lesson", to: SECURITY_LESSON_HREF, label: "最初のレッスンを読む", external: true };
/** 帯の「セキュリティ」の入口を光らせる、窓の中の子 (最初のレッスンの行も含む) の id */
export const SECURITY_MENU_ACTIVE_IDS: ReadonlySet<string> = new Set([...SECURITY_MENU_ITEMS.map((i) => i.id), SECURITY_MENU_MORE.id]);

/*
2026-10-25 の代表の決め。帯に「IT資格取得」を足して 8 項目になり 1152px に収まらなかったので、印を外す代わりに
技術面接問題とコーディング面接問題を帯では「面接対策」の 1 つにまとめた (行き先は /interview-questions)。
2026-10-26 の代表の言葉「面接対策も同じように 技術面接 コーディング面接 の 2 つに分けたらいい」で、
「面接対策」を転職と同じ窓にし、中を「技術面接」と「コーディング面接」の 2 つにした。引き出しも同じ 2 つの組にする
*/
export const INTERVIEW_MENU_ITEMS: HeaderNavItem[] = [
  { id: "interview", to: "/interview-questions", label: "技術面接", description: "よく聞かれる問いに答える練習" },
  { id: "coding", to: CODING_INTERVIEW_HREF, label: "コーディング面接", description: "コードを書いて解く面接の問題" },
];
/** 帯の「面接対策」を光らせる id (resolveActiveNav が返す、技術面接とコーディング面接) */
export const INTERVIEW_ACTIVE_IDS: ReadonlySet<string> = new Set(INTERVIEW_MENU_ITEMS.map((i) => i.id));

/*
2026-10-26 の代表の指示。「IT資格」も転職と同じ窓にして、中から直に試験を選べるようにする。
中に並べる 5 つは、資格の入口の頁のよくある問い「IT資格はどれから受ければいいですか」が挙げる最初の 1 つ
(apps/client の lib/certification.ts の CERTIFICATION_FAQ)。未経験の入口のITパスポートと基本情報、
サーバーとクラウドの LinuC と AWS クラウドプラクティショナー、セキュリティの情報セキュリティマネジメント。
並びはその答えの順。窓の下に、残りの試験も含む一覧 (/certification) への行を置く
*/
const CERTIFICATION_MENU_PICKS: readonly { slug: string; label: string; description: string }[] = [
  { slug: "it-passport", label: "ITパスポート", description: "未経験の最初の 1 つに向く国家試験" },
  { slug: "fe", label: "基本情報技術者", description: "作る人の基本の国家試験" },
  { slug: "linuc", label: "LinuC レベル1", description: "Linux サーバーの操作と管理" },
  { slug: "aws-clf", label: "AWS クラウドプラクティショナー", description: "クラウドの入口の AWS 認定" },
  { slug: "sg", label: "情報セキュリティマネジメント", description: "守るルールと運用を問う国家試験" },
];
export const CERTIFICATION_MENU_ITEMS: HeaderNavItem[] = CERTIFICATION_MENU_PICKS.map((p) => ({
  id: `certification-${p.slug}`,
  to: certificationExamHref(p.slug),
  label: p.label,
  description: p.description,
}));
/** 窓の下の「すべての資格を見る」。窓に並べない試験の頁 (/certification/ccna など) でも入口を光らせるための id も兼ねる */
export const CERTIFICATION_MENU_MORE: HeaderNavItem = { id: "certification", to: CERTIFICATION_HREF, label: "すべての資格を見る" };

/*
2026-10-26 の代表の決め (名前と並び)。左に学ぶ物、右へ進むほど申し込みに近い物を置き、面接対策 → 転職 → 料金 →
新規登録の釦と並べる。名前は人が検索する短い語 (Google 広告の月の検索数で IT資格 8,100 と資格取得 5,400、
AIコーディング 3,600 で住所の /ai-coding とも合う、面接対策 5,400)。印は項目ごとに今までの物のまま
*/
/*
2026-10-26 の代表の指示 (1 回目)。ゆめさく CS (コンピュータサイエンスの学びの場) を、帯の「コース」の窓の下と、引き出しの「学ぶ」の組に 1 行足す。
帯の最上段には足さない。PC の窓の行は apps/client の CoursesMenuPanel がこの項目を読んで描く。

同じ日の 2 回目の指示「コンピュータサイエンスはヘッダーに独立して作ってほしい」で、帯の最上段にも
「セキュリティ」の隣に独立した項目を足し、IT資格・セキュリティと同じ hover の窓にした
(courses.activeFor は外し、コースの窓の下の行も削った。CoursesMenuPanel.tsx を見る)。
この COMPUTER_SCIENCE_NAV 自体は、引き出しの「学ぶ」の組の素のリンクとしてだけ残す
*/
export const COMPUTER_SCIENCE_NAV: HeaderNavItem = {
  id: "computer-science",
  to: "/computer-science",
  label: "コンピュータサイエンス",
  description: "計算の理論から OS・ネットワークまでの地図",
};

/*
2026-10-30 に数学 (/maths) とエンジニアの英語 (/english) を帯の窓「数学・英語」にまとめたが、
2026-11-03 の代表の言葉「数学と英語は header に入れなくていいよ」で、帯と引き出しから外した。
2 つの頁へはフッターの「主な機能」から行ける (PageFooter が MATHS_ENGLISH_MENU_ITEMS を並べる)
*/
export const MATHS_NAV: HeaderNavItem = {
  id: "maths",
  to: "/maths",
  label: "数学",
  description: "対数、整数、確率、行列を Python で確かめる",
};
export const ENGLISH_NAV: HeaderNavItem = {
  id: "english",
  to: "/english",
  label: "エンジニアの英語",
  description: "エラー文、公式文書、PR の英語を学ぶ",
};
export const MATHS_ENGLISH_MENU_ITEMS: HeaderNavItem[] = [MATHS_NAV, ENGLISH_NAV];

/*
帯の「コンピュータサイエンス」の窓の中身。apps/client/app/lib/computer-science.ts (CS_TRACKS) から選んだ、
今読める物だけ。準備中の道 (page: false。基礎、機械学習、ネットワーク、データ、言語の 5 本) は窓に並べない。
並べるのは入口 (学ぶ順)、入口の中の 2 つの節 (分野の地図、実例)、頁を持つ唯一の道 (アルゴリズムとデータ構造)。
行き先はどれも apps/client の中の SPA の住所 (/computer-science 配下) なので、セキュリティと違い external にしない
*/
const CS_MENU_PICKS: readonly { id: string; to: string; label: string; description: string }[] = [
  { id: "computer-science-learn", to: COMPUTER_SCIENCE_NAV.to, label: "学ぶ順", description: "6 本の道を仕事で使う順に" },
  { id: "computer-science-fields", to: `${COMPUTER_SCIENCE_NAV.to}#fields`, label: "分野の地図", description: "基礎と実装の分野を地図で見る" },
  { id: "computer-science-algorithms", to: `${COMPUTER_SCIENCE_NAV.to}/algorithms`, label: "アルゴリズムとデータ構造", description: "探す・並べる・たどるを動かして学ぶ" },
  { id: "computer-science-cases", to: `${COMPUTER_SCIENCE_NAV.to}#cases`, label: "実例", description: "CS の知識が足りず起きた事を引く" },
];
export const COMPUTER_SCIENCE_MENU_ITEMS: HeaderNavItem[] = CS_MENU_PICKS.map((p) => ({ ...p }));
/**
 * 帯の「コンピュータサイエンス」の入口を光らせる id。窓の 4 つに加え、引き出し「学ぶ」の組に残した
 * COMPUTER_SCIENCE_NAV (素のリンク、to は窓の「学ぶ順」と同じ /computer-science) も、resolveActiveNav の
 * 住所あわせで同じ id を返しうるので含める (学ぶ順と道の頁の 2 つの住所にしかならないため、窓の 4 つを
 * 住所だけで見分けることはできない。見分けるのは頁の中の CsTabs の仕事で、帯は入口が光れば足りる)
 */
export const COMPUTER_SCIENCE_MENU_ACTIVE_IDS: ReadonlySet<string> = new Set([...COMPUTER_SCIENCE_MENU_ITEMS.map((i) => i.id), COMPUTER_SCIENCE_NAV.id]);

/**
 * 帯の主な入口を、窓を開いた平たいリンクの列にしたもの。フッターの「主な機能」と、学習の記事の頁の終わりの入口が使う。
 * 面接対策の窓は技術面接とコーディング面接の 2 つに分け (2026-10-25)、帯から外した数学とエンジニアの英語は
 * コンピュータサイエンスの次に置いて入口を残す (2026-11-03)。IT資格と転職の窓は入口の住所 1 つだけを置く
 */
export function buildSiteEntryLinks({ pricingHref, exclude }: { pricingHref: string; exclude?: ReadonlySet<string> }): HeaderNavItem[] {
  return buildHeaderNav({ pricingHref })
    .bar.filter((item) => !exclude?.has(item.id))
    .flatMap((item) =>
      item.id === "interview-menu" ? INTERVIEW_MENU_ITEMS : item.id === "computer-science-menu" ? [item, ...MATHS_ENGLISH_MENU_ITEMS] : [item],
    );
}

export function buildHeaderNav({ pricingHref }: { pricingHref: string }): HeaderNav {
  const courses: HeaderNavItem = { id: "courses", to: "/learn/courses", label: "コース", icon: "curriculum" };
  const ai: HeaderNavItem = { id: "ai", to: "/ai-coding", label: "AIコーディング", icon: "projects" };
  // 帯の印 (NavIcon) に賞状や認定の印は無い (2026-10-25 に 9 つを見た)。いちばん近い「開いた教科書」を使う。贈り物 (what-you-get) は資格に読めなかった
  const certification: HeaderNavItem = {
    id: "certification-menu",
    to: CERTIFICATION_HREF,
    label: "IT資格",
    icon: "curriculum",
    isMenuTrigger: true,
    menu: {
      label: "IT資格の入口",
      items: CERTIFICATION_MENU_ITEMS,
      more: CERTIFICATION_MENU_MORE,
      // 窓に並べない試験の頁は resolveActiveNav が一覧の行 (certification) を返すので、それも含める
      activeIds: new Set([...CERTIFICATION_MENU_ITEMS.map((i) => i.id), CERTIFICATION_MENU_MORE.id]),
    },
  };
  // 窓の入口。to は中の「学ぶ順」と同じ /security/。isMenuTrigger で resolveActiveNav の住所あわせから外れるので引き分けは起きない
  const security: HeaderNavItem = {
    id: "security-menu",
    to: SECURITY_TRACK_HREF,
    label: "セキュリティ",
    icon: "security",
    external: true,
    isMenuTrigger: true,
    menu: { label: "セキュリティの入口", items: SECURITY_MENU_ITEMS, more: SECURITY_MENU_MORE, activeIds: SECURITY_MENU_ACTIVE_IDS },
  };
  // 窓の入口。to は中の「学ぶ順」と同じ /computer-science。帯の位置はセキュリティの隣 (2026-10-26 の代表の指示)
  const computerScience: HeaderNavItem = {
    id: "computer-science-menu",
    to: COMPUTER_SCIENCE_NAV.to,
    label: "コンピュータサイエンス",
    // 帯では 11 字が幅を取りすぎて 9 項目が 1280px に収まらないので「CS」と出す (2026-10-30 の代表の決め、案 B)
    barLabel: "CS",
    // NavIcon に学問・教科の専用の印は無い (2026-10-25 に 9 つを見た)。IT資格と同じ「開いた教科書」を使い回す
    icon: "curriculum",
    isMenuTrigger: true,
    menu: { label: "コンピュータサイエンスの入口", items: COMPUTER_SCIENCE_MENU_ITEMS, activeIds: COMPUTER_SCIENCE_MENU_ACTIVE_IDS },
  };
  // 窓の入口。to は技術面接の正の入口 /interview-questions。押せばそこへ行く
  const interview: HeaderNavItem = {
    id: "interview-menu",
    to: "/interview-questions",
    label: "面接対策",
    icon: "counseling",
    isMenuTrigger: true,
    menu: { label: "面接対策の入口", items: INTERVIEW_MENU_ITEMS, activeIds: INTERVIEW_ACTIVE_IDS },
  };
  // 窓の入口。to は中の「転職ロードマップ」と同じ住所だが、isMenuTrigger で resolveActiveNav の
  // 住所あわせから外しているので、長さの引き分けは起きない
  const career: HeaderNavItem = {
    id: "career-menu",
    to: "/career-change",
    label: "転職",
    icon: "trial",
    isMenuTrigger: true,
    menu: { label: "転職の入口", items: CAREER_MENU_ITEMS, activeIds: CAREER_MENU_ACTIVE_CHILD_IDS },
  };
  const pricing: HeaderNavItem = { id: "pricing", to: pricingHref, label: "料金", icon: "cost" };
  return {
    bar: [courses, ai, certification, security, computerScience, interview, career, pricing],
    /*
    引き出しは帯と同じ分け方にする。帯で窓を持つ 4 つ (IT資格、セキュリティ、面接対策、転職) は、窓の中身をそのまま 1 つの組にする。
    タイピングは前の「腕試し」の組から「学ぶ」へ移した (面接の 2 つが「面接対策」の組へ移り、1 つだけ残るため)。
    セキュリティは 2026-10-26 に窓になり、「学ぶ」組の素のリンクから自分の組 (SECURITY_MENU_ITEMS) へ移した。
    コンピュータサイエンスは帯では窓になったが (COMPUTER_SCIENCE_MENU_ITEMS)、引き出しは代表の指示で今の
    1 行 (COMPUTER_SCIENCE_NAV) のまま「学ぶ」組に残す。窓の 4 つの組は引き出しには作らない
    */
    drawer: [
      { id: "learn", label: "学ぶ", items: [courses, COMPUTER_SCIENCE_NAV, ai, { id: "guides", to: "/guides", label: "学習ガイド" }, { id: "typing", to: "/typing", label: "タイピング" }] },
      { id: "certification", label: "IT資格", items: [...CERTIFICATION_MENU_ITEMS, { ...CERTIFICATION_MENU_MORE, wide: true }] },
      // 絵の無い 2 つ (事例、ハッキングを合法に学ぶ) も 1 行を 1 つで使う。半分の幅では「ハッキングを合法に学ぶ」が折れ目を持たず右で切れた (2026-11-03)
      { id: "security", label: "セキュリティ", items: [...SECURITY_MENU_ITEMS.map((i) => ({ ...i, wide: true })), { ...SECURITY_MENU_MORE, wide: true }] },
      { id: "interview", label: "面接対策", items: INTERVIEW_MENU_ITEMS },
      { id: "work", label: "転職", items: CAREER_MENU_ITEMS },
      { id: "lookup", label: "調べる", items: [{ id: "glossary", to: "/glossary", label: "用語辞典" }, { id: "git", to: "/git", label: "Git コマンド辞典" }] },
      { id: "plan", label: "", items: [pricing] },
    ],
  };
}

/**
 * 今いる頁がどの項目に当たるか。どれにも当たらなければ null (何も光らせない)。
 * 住所が長く一致する項目を先に見る (/interview-questions/coding は技術面接問題でなくコーディング面接問題)。
 * 帯の項目と引き出しの項目の両方を対象にする
 */
export function resolveActiveNav(path: string, nav: HeaderNav, pricingHref: string): string | null {
  // 行き先が / で終わる項目 (SECURITY_TRACK_HREF の "/security/") は、その下の頁 (/security/lessons/xss/) でも光らせる。
  // 前は `${to}/` で見ていて "/security//" になり、入口の 1 頁でしか光らなかった
  const hit = (to: string) => to !== "/" && (path === to || path.startsWith(to.endsWith("/") ? to : `${to}/`));
  // 窓の入口 (isMenuTrigger) は候補から外す。入口の to は中の子と同じ住所になりがちで、
  // 外さないと「どちらが長く一致するか」が引き分けになる。入口自体を光らせる判定は PageHeader 側でやる
  const all = [...nav.bar, ...nav.drawer.flatMap((g) => g.items)].filter((i) => !i.isMenuTrigger);
  const best = all.filter((i) => hit(i.to)).sort((x, y) => y.to.length - x.to.length)[0];
  if (best) return best.id;
  // コース一覧は /learn の下と旧 /courses でも光らせる
  if (path.startsWith("/courses") || path === "/learn" || path.startsWith("/learn/")) return "courses";
  // 渡された行き先に加えて /pricing も現在地として見る。外から旧 URL で来た人のため
  if (path.startsWith(pricingHref) || path.startsWith("/pricing")) return "pricing";
  return null;
}

/*
姉妹サイトの入口。PC とスマホの帯の上の細い段と、スマホの引き出しの下に出す。
まなび部 (/community) は 2026-10-20 に代表の指示で外した。
女性向けは別アプリなので素の a で別タブ。行き先は women.yumesaku.tech (2026-10-22 の決めで、
こちらが index の正。yumesaku.tech/women は残して noindex にする側)
*/
export const WOMEN_SITE_URL = "https://women.yumesaku.tech/";

/*
紹介リンク (?ref=) で来た人の紹介コードは、yumesaku.tech の localStorage (shegate_ref_code) に置かれる
(lab-astro の referral.ts と apps/client の referral-landing)。前の行き先 /women は同じ origin なので
女性向けの頁がそのまま読めたが、women.yumesaku.tech は別の origin で読めない。
押したときに、期限内のコードを ?ref= に付けて渡す (女性向けの useRefCode が ?ref= を受けて保存する)
*/
const REF_STORAGE_KEY = "shegate_ref_code";
const REF_EXPIRES_KEY = "yumesaku_ref_expires";
const REF_CODE_PATTERN = /^[A-Za-z0-9]{4,10}$/;

export function withStoredReferral(href: string, storage: Pick<Storage, "getItem"> | null, now: number = Date.now()): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }
  // 前に押したときに付けた ?ref= を先に外す。期限が切れたあとまで古いコードを渡さない
  url.searchParams.delete("ref");
  try {
    const code = storage?.getItem(REF_STORAGE_KEY);
    // 期限の印は「コード:エポックのミリ秒」。印が無い、別のコード、数でない、期限切れなら付けない
    const [markCode, until] = (storage?.getItem(REF_EXPIRES_KEY) ?? "").split(":");
    const untilMs = Number(until);
    if (code && REF_CODE_PATTERN.test(code) && markCode === code && Number.isFinite(untilMs) && untilMs > now) {
      url.searchParams.set("ref", code);
    }
  } catch {
    // 読めない窓。コードを付けずに開く
  }
  return url.toString();
}

/**
 * 女性向けの入口に触れたときに呼ぶ。開く直前に、紹介コードを付けた行き先へ差し替える。
 * 押す (click)、中の釦 (auxclick) のほか、長押しのメニューから別タブで開く道のために、指が触れた時点 (pointerdown) と
 * 右の釦のメニュー (contextmenu) でも差し替える
 */
export function attachReferralOnOpen(event: { currentTarget: HTMLAnchorElement }) {
  let storage: Storage | null = null;
  try {
    storage = window.localStorage;
  } catch {
    storage = null;
  }
  event.currentTarget.href = withStoredReferral(event.currentTarget.href, storage);
}

export interface HeaderSisterLink {
  href: string;
  label: string;
  /** 別のアプリ。素の a で別タブに開く */
  external?: boolean;
}

export const HEADER_SISTER_LINKS: HeaderSisterLink[] = [
  { href: "/lab", label: "ゆめさくエンジニア研究所" },
  { href: WOMEN_SITE_URL, label: "女性向けエンジニアスクール", external: true },
];
